import { IconArrowsSort } from '@tabler/icons-react'
import { ReactNode, useDeferredValue, useState } from 'react'

import { AstraButton, AstraIcon, AstraSegmentedControl, AstraTooltip } from '~/astra'
import { SearchText } from '~/components'
import { useSessionLikeSearchParam } from '~/hooks/useSessionLikeSearchParam'

import { Section } from '../Section'
import { useResourceNavigation } from '../useResourceNavigation'
import { EditPractitioner } from './Form/EditPractitioner'
import { NewPractitioner } from './Form/NewPractitioner'
import { ReorderSurgeonsModal } from './Reorder/ReorderSurgeonsModal'
import { DipsSurgeonsTable, PractitionersTable } from './SurgeonsTable'
import { useSurgeonTableViewModel } from './useSurgeonTableViewModel'

const surgeonTabs = ['active', 'inactive', 'fromDips'] as const
type SurgeonTab = (typeof surgeonTabs)[number]
const tabOptions: { value: SurgeonTab; label: string }[] = [
    { value: 'active', label: 'Aktive' },
    { value: 'inactive', label: 'Inaktive' },
    { value: 'fromDips', label: 'Hentet fra DIPS' },
]

const isSurgeonTab = (value: string | undefined | null): value is SurgeonTab => !!value && surgeonTabs.includes(value as SurgeonTab)

const getSurgeonTab = (tabValue: string | null): SurgeonTab => {
    if (isSurgeonTab(tabValue)) return tabValue
    return surgeonTabs[0]
}

export const SurgeonTables = () => {
    const [activeTab, setActiveTab] = useSessionLikeSearchParam('surgeonTab', getSurgeonTab)
    const [searchNeedle, setSearchNeedle] = useState('')
    const [reorderOpen, setReorderOpen] = useState(false)
    const searchValue = useDeferredValue(searchNeedle)

    const { activePractitioners, inactivePractitioners, dipsSurgeons } = useSurgeonTableViewModel({ search: searchValue })

    const tableByTab: Record<SurgeonTab, { label: string; content: ReactNode }> = {
        active: {
            label: 'Aktive kirurger',
            content: <PractitionersTable practitioners={activePractitioners} />,
        },
        inactive: {
            label: 'Inaktive kirurger',
            content: <PractitionersTable practitioners={inactivePractitioners} />,
        },
        fromDips: {
            label: 'Kirurger hentet fra DIPS',
            content: <DipsSurgeonsTable resources={dipsSurgeons} />,
        },
    }

    return (
        <div className="flex h-full w-full flex-col gap-4 overflow-auto">
            <div>
                <AstraSegmentedControl value={activeTab} onValueChange={value => setActiveTab(value as SurgeonTab)} size="medium" controlOptions={tabOptions} />
            </div>
            <Section
                title={tableByTab[activeTab].label}
                afterTitle={
                    <div className="ml-auto flex items-center gap-2">
                        <SearchText rawNeedle={searchNeedle} setNeedle={setSearchNeedle} placeholder="Søk etter kirurger" />
                        {activeTab !== 'fromDips' && (
                            <AstraTooltip tooltipContent="Endre rekkefølge på kirurger">
                                <AstraButton variant="secondary" icon={<AstraIcon icon={IconArrowsSort} />} onClick={() => setReorderOpen(true)} />
                            </AstraTooltip>
                        )}
                    </div>
                }
            >
                {tableByTab[activeTab].content}
                <ReorderSurgeonsModal onClose={() => setReorderOpen(false)} isOpen={reorderOpen} />
            </Section>
        </div>
    )
}

export const Surgeons = () => {
    const { entityId: practitionerId, dipsResourceId } = useResourceNavigation()

    if (practitionerId === null && dipsResourceId === null) {
        return <SurgeonTables />
    }
    if (practitionerId !== null) {
        return <EditPractitioner />
    }
    if (dipsResourceId !== null) {
        return <NewPractitioner />
    }
    return null
}
